import React, { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Trash2 } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import CartItem from '../../components/wreath/CartItem';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';
import { formatCurrency } from '../../utils/formatters';
import {
  calculateShipping,
  FREE_SHIPPING_THRESHOLD,
} from '../../utils/constants';

const CartPage = () => {
  const navigate = useNavigate();
  const {
    cartItems,
    totalPrice,
    getTotalItems,
    removeFromCart,
    removeMany,
    clearCart,
    restoreItems,
    getLineKey,
  } = useCart();
  const { showSuccess, showInfo } = useToast();

  const [selectedKeys, setSelectedKeys] = useState([]);
  // 삭제 확인 모달: { type: 'single'|'selected'|'all', item? }
  const [confirm, setConfirm] = useState(null);

  const shippingCost = calculateShipping(totalPrice);
  const finalPrice = totalPrice + shippingCost;

  const remainingForFree = Math.max(0, FREE_SHIPPING_THRESHOLD - totalPrice);
  const progressPercent = useMemo(() => {
    if (totalPrice <= 0) return 0;
    return Math.min(100, Math.round((totalPrice / FREE_SHIPPING_THRESHOLD) * 100));
  }, [totalPrice]);

  const allKeys = useMemo(() => cartItems.map(getLineKey), [cartItems, getLineKey]);
  const allSelected = selectedKeys.length > 0 && selectedKeys.length === allKeys.length;

  const toggleSelectAll = () => {
    setSelectedKeys(allSelected ? [] : allKeys);
  };

  const toggleSelect = (key) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleContinueShopping = () => navigate('/wreaths');
  const handleCheckout = () => navigate('/checkout');

  // Undo 버퍼 (토스트에 버튼이 없으므로 별도 "되돌리기" 버튼으로 노출)
  const undoBuffer = useRef([]);
  const [canUndo, setCanUndo] = useState(false);

  // 삭제 실행 + Undo 토스트
  const performRemove = () => {
    if (!confirm) return;

    if (confirm.type === 'single') {
      const removed = confirm.item;
      removeFromCart(getLineKey(removed));
      undoBuffer.current = [removed];
      showInfo('상품을 삭제했습니다.');
    } else if (confirm.type === 'selected') {
      const removed = cartItems.filter((it) => selectedKeys.includes(getLineKey(it)));
      removeMany(selectedKeys);
      undoBuffer.current = removed;
      setSelectedKeys([]);
      showInfo(`${removed.length}개 상품을 삭제했습니다.`);
    } else if (confirm.type === 'all') {
      const removed = [...cartItems];
      clearCart();
      undoBuffer.current = removed;
      setSelectedKeys([]);
      showInfo('장바구니를 비웠습니다.');
    }

    setConfirm(null);
  };

  // performRemove 직후 undo 가능 상태 갱신
  const handleConfirm = () => {
    performRemove();
    setCanUndo(true);
    window.setTimeout(() => setCanUndo(false), 8000);
  };

  const handleUndo = () => {
    if (undoBuffer.current.length > 0) {
      restoreItems(undoBuffer.current);
      showSuccess('삭제를 취소했습니다.');
      undoBuffer.current = [];
      setCanUndo(false);
    }
  };

  const confirmMessage =
    confirm?.type === 'single'
      ? `'${confirm.item?.name}' 상품을 장바구니에서 삭제하시겠습니까?`
      : confirm?.type === 'selected'
      ? `선택한 ${selectedKeys.length}개 상품을 삭제하시겠습니까?`
      : '장바구니의 모든 상품을 삭제하시겠습니까?';

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* 페이지 헤더 */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-primary mb-2">장바구니</h1>
          <p className="text-accent-600">{getTotalItems()}개의 상품이 담겨있습니다</p>
        </div>

        {cartItems.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                <ShoppingBag size={40} className="text-accent-400" />
              </div>
              <h2 className="text-xl font-bold text-primary mb-2">장바구니가 비어있습니다</h2>
              <p className="text-accent-600 mb-6">화환을 선택하여 장바구니에 담아보세요</p>
              {canUndo && undoBuffer.current.length > 0 && (
                <div className="mb-4">
                  <Button variant="outline" onClick={handleUndo}>
                    방금 삭제 되돌리기
                  </Button>
                </div>
              )}
              <Button variant="primary" onClick={handleContinueShopping}>
                화환 둘러보기
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {/* 장바구니 아이템 목록 */}
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white rounded-lg border border-accent-200 p-4 mb-2 flex items-center justify-between flex-wrap gap-3">
                <label className="flex items-center gap-2 text-sm font-medium text-primary cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-primary rounded"
                  />
                  전체 선택 ({cartItems.length}개)
                </label>
                <div className="flex items-center gap-2">
                  {canUndo && undoBuffer.current.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={handleUndo}>
                      되돌리기
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirm({ type: 'selected' })}
                    disabled={selectedKeys.length === 0}
                  >
                    선택 삭제
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirm({ type: 'all' })}
                    className="flex items-center gap-1 text-red-600"
                  >
                    <Trash2 size={16} />
                    전체 비우기
                  </Button>
                </div>
              </div>

              {cartItems.map((item) => {
                const key = getLineKey(item);
                return (
                  <div key={key} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(key)}
                      onChange={() => toggleSelect(key)}
                      aria-label={`${item.name} 선택`}
                      className="mt-6 w-4 h-4 text-primary rounded flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <CartItem
                        item={item}
                        onRequestRemove={(it) => setConfirm({ type: 'single', item: it })}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 주문 요약 */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border border-accent-200 p-6 sticky top-4">
                <h2 className="text-xl font-bold text-primary mb-6">주문 요약</h2>

                {/* 무료배송 진행 바 */}
                <div className="mb-6">
                  {remainingForFree > 0 ? (
                    <p className="text-xs text-accent-700 mb-2">
                      <span className="font-bold text-primary">
                        {formatCurrency(remainingForFree)}
                      </span>{' '}
                      더 담으면 무료배송!
                    </p>
                  ) : (
                    <p className="text-xs text-primary font-bold mb-2">무료배송 적용되었습니다</p>
                  )}
                  <div
                    className="h-2 w-full rounded-full bg-accent-100 overflow-hidden"
                    role="progressbar"
                    aria-valuenow={progressPercent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between text-accent-700">
                    <span>상품 금액</span>
                    <span className="font-medium">{formatCurrency(totalPrice)}</span>
                  </div>

                  <div className="flex items-center justify-between text-accent-700">
                    <span>배송비</span>
                    <span className="font-medium">
                      {shippingCost === 0 ? (
                        <span className="text-primary font-bold">무료</span>
                      ) : (
                        formatCurrency(shippingCost)
                      )}
                    </span>
                  </div>

                  <div className="border-t border-accent-200 my-4"></div>

                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-primary">최종 결제 금액</span>
                    <span className="text-2xl font-bold text-primary">
                      {formatCurrency(finalPrice)}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button variant="primary" size="lg" fullWidth onClick={handleCheckout}>
                    주문하기
                  </Button>
                  <Button variant="outline" size="lg" fullWidth onClick={handleContinueShopping}>
                    쇼핑 계속하기
                  </Button>
                </div>

                <div className="mt-6 p-4 bg-accent-50 rounded-lg border border-accent-200">
                  <h3 className="font-bold text-primary mb-2 text-sm">주문 안내</h3>
                  <ul className="text-xs text-accent-700 space-y-1">
                    <li>• 주문 후 2-3시간 내 배송</li>
                    <li>• 장례식장 직접 배송</li>
                    <li>• 주문 확인 후 즉시 제작</li>
                    <li>• 배송 전 확인 연락</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
        title="장바구니 삭제"
        message={confirmMessage}
        confirmLabel="삭제"
        confirmVariant="danger"
      />
    </MainLayout>
  );
};

export default CartPage;
